import AuthSyncer from '@/lib/Syncing/Syncers/AuthSyncer';
import EposDataSyncer from '@/lib/Syncing/Syncers/EposDataSyncer';
import ItemsSyncer from '@/lib/Syncing/Syncers/ItemsSyncer';
import KitchenDataSyncer from '@/lib/Syncing/Syncers/KitchenDataSyncer';
import logFactory from '@/libraries/DigiTickets/Logging/logFactory';
import PrivilegesSyncer from '@/lib/Syncing/Syncers/PrivilegesSyncer';

export class SyncRunner {
    constructor(
        $injector
    ) {
        this.$injector = $injector;

        this.log = logFactory('SyncRunner');
    }

    /**
     * @param {{syncer: AbstractSyncer.constructor|AbstractSyncer, branch?: ?Branch}[]} syncers Which syncers to run.
     *
     * @returns {Promise}
     */
    async runSyncers(syncers) {
        this.resetStatus();

        // Run each of the syncers sequentially.
        for (let i = 0; i < syncers.length; i++) {
            const syncerInstance = this.getSyncerInstance(syncers[i].syncer);

            this.log.debug('Running syncer', syncerInstance, 'with branch', syncers[i].branch);

            try {
                // If we immediately set the 'syncing' flag, we can end up with a quick flash of the syncing
                // progress overlay in the UI if there is nothing to actually sync.
                // To prevent this we set the syncing flag after a small delay.
                // If the sync finishes faster than this (<50ms) the overlay will never be shown.
                let setSyncingTimeout;

                setSyncingTimeout = setTimeout(() => {
                    this.setSyncing(true);
                    setSyncingTimeout = null;
                }, 50);

                await syncerInstance.run(
                    syncers[i].branch,
                    (progress) => {
                        this.setProgress(progress);
                    }
                );

                if (setSyncingTimeout !== null) {
                    clearTimeout(setSyncingTimeout);
                    setSyncingTimeout = null;
                }
            } catch (e) {
                this.log.error('Error running syncer', syncerInstance, e);
                this.setError(e);
                throw e;
            }

            this.setSyncing(false);
        }
    }

    /**
     * @param {AbstractSyncer.constructor|AbstractSyncer} className
     *
     * @return {AbstractSyncer}
     */
    getSyncerInstance(className) {
        return this.$injector.instantiate(className);
    }

    /**
     * @private
     */
    resetStatus() {
        this.getStore().commit('sync/reset');
    }

    /**
     * @private
     * @param {boolean} value
     */
    setSyncing(value) {
        this.getStore().commit('sync/setSyncing', value);
    }

    /**
     * @private
     * @param {?Error} value
     */
    setError(value) {
        this.getStore().commit('sync/setError', value);
    }

    /**
     * @private
     * @param {any} value
     */
    setProgress(value) {
        this.getStore().commit('sync/setProgress', value);
    }

    /**
     * @private
     * @return {Store<any>}
     */
    getStore() {
        return window.rootVueStore;
    }

    syncPrivileges() {
        return this.runSyncers([
            {
                syncer: PrivilegesSyncer,
            },
        ]);
    }

    /**
     * @param {UserService} userService
     * @return {Promise}
     */
    syncEposStartupData(userService) {
        return this.runSyncers([
            {
                syncer: AuthSyncer,
            },
            {
                syncer: EposDataSyncer,
                branch: userService.currentBranch,
            },
            {
                syncer: ItemsSyncer,
                branch: userService.currentItemBranch,
            },
        ]);
    }

    /**
     * @param {UserService} userService
     * @return {Promise}
     */
    syncKitchenData(userService) {
        return this.runSyncers([
            {
                syncer: KitchenDataSyncer,
                branch: userService.currentBranch,
            },
        ]);
    }

    /**
     * @param {UserService} userService
     * @return {Promise}
     */
    syncItems(userService) {
        return this.runSyncers([
            {
                syncer: ItemsSyncer,
                branch: userService.currentItemBranch,
            },
        ]);
    }
}
